import {
  AfterViewInit,
  Component,
  ViewChild,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { lastValueFrom } from 'rxjs';

import { ActionClick, GridColumn, RowClick } from "../../models/data-table.interface";
import { ConfirmationDialogService } from '../confirmation-dialog/services/confirmation-dialog.service';

interface DataTable {
  pastAppointment?: boolean;
  hasTranscription?: boolean;
  isDeleted?: boolean;
}

@Component({
    standalone: false,
    selector: 'cva-data-table',
    templateUrl: './data-table.component.html',
    styleUrl: './data-table.component.scss',
    
})
export class DataTableComponent<T extends DataTable> implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  @Input() displayedColumns: GridColumn<T>[] = [];
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 15, 20, 25, 30];
  @Input() tableData!: T[];
  @Input() tableClass: string = "mat-elevation-z8";
  @Input() isEditable: boolean = false;
  @Input() isDeletable: boolean = false;
  @Input() isRowClickable: boolean = false;
  @Input() deleteConfirmationMessage: string = 'Are you sure you want to delete?';
  @Input() stickyColumnIndex: number = -1;
  @Input() showPagination: boolean = true;
  @Input() skipConfirmationPopup: boolean = false;
  @Input() isToTitleCaseColumn: boolean = false;
  @Input() maxLength: number = 100;
  @Input() hidePageSize: boolean = false;

  @Output() rowClick: EventEmitter<RowClick<T>> = new EventEmitter<RowClick<T>>();
  @Output() actionClick: EventEmitter<ActionClick<T>> = new EventEmitter<ActionClick<T>>();

  dataSource = new MatTableDataSource<T>();
  allColumns: string[] = []

  constructor(
    private readonly confirmationDialogService: ConfirmationDialogService
  ) { }


  ngOnInit() {
    this.displayedColumns = this.displayedColumns.filter(col => !col.walkin);
    this.allColumns = this.displayedColumns.map(col => col.field);
    if (this.isEditable || this.isDeletable) {
      this.allColumns.push('action');
    }
    this.dataSource = new MatTableDataSource<T>(this.tableData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    //update the data when there is change & change detected not for first time
    if (changes['tableData'] && !changes['tableData'].firstChange) {
      this.dataSource = new MatTableDataSource<T>(this.tableData);
      this.dataSource.sort = this.sort;
      if (this.showPagination && this.paginator) {
        this.dataSource.paginator = this.paginator;
        //reset to first page
        this.paginator.firstPage();
      }
    }

    if (changes['displayedColumns'] && !changes['displayedColumns'].firstChange) {
      this.allColumns = this.displayedColumns.map(col => col.field);
    }
  }

  onRowClicked(rowData: T): void {
    this.rowClick.emit({ rowData });
  }

  onAction(event: MouseEvent, rowData: T, action: string) {
    event.stopPropagation();
    if (this.isDeletable && action === 'delete' && (rowData?.hasTranscription || rowData?.pastAppointment)) {
      return;
    }
    if (action === 'delete' && !this.skipConfirmationPopup) {
      const confirmed$ = this.confirmationDialogService.openConfirmDialog(this.deleteConfirmationMessage)
      lastValueFrom(confirmed$).then((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.actionClick.emit({ rowData, action });
        }
      })
    } else {
      this.actionClick.emit({ rowData, action });
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    if (this.showPagination && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  //This function is just to avoid SONAR warning
  doNothing() {
    console.log("Do nothing");
  }
}
